import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Template from '../Components/Template';
import { useSaveExamFormMutation } from '../Services/modules/exam';
import { Config } from '../Config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const TSE_NAME = 'गुणवत्ता शोध परीक्षा';
const SSP_NAME = 'शताब्दी शिष्यवृत्ती परीक्षा';

const MARATHI_REGEX = /^[ऀ-ॿ\s.]+$/;
const marathiOnly = (val) => val.replace(/[^ऀ-ॿ\s.]/g, '');
const digitsOnly = (val) => val.replace(/\D/g, '');

const TSE_STANDARDS = ['1', '2', '3', '5', '6', '8'];
const SSP_STANDARDS = ['4', '7'];

function calculateFee(standard) {
  if (!standard) return 0;
  if (['1', '2'].includes(standard)) return 400;
  if (['3', '5', '6', '8'].includes(standard)) return 450;
  if (['4', '7'].includes(standard)) return 400;
  return 0;
}

function ExamFormPage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const examParam = queryParams.get('exam');

  const examName = examParam === 'tse' ? TSE_NAME : examParam === 'shatabdi' ? SSP_NAME : '';
  const standards = examParam === 'tse' ? TSE_STANDARDS : examParam === 'shatabdi' ? SSP_STANDARDS : [];

  const [exam_standard, setExamStandard] = useState('');
  const [gender, setGender] = useState('');
  const [first_name, setFirstName] = useState('');
  const [middle_name, setMiddleName] = useState('');
  const [last_name, setLastName] = useState('');
  const [mobile_number, setMobileNumber] = useState('');

  const [schoolInput, setSchoolInput] = useState('');
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [schoolSelected, setSchoolSelected] = useState(false);
  const [selectedSchoolName, setSelectedSchoolName] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const searchTimerRef = useRef(null);

  const [SaveExamForm, { data: saveData, isSuccess: saveSuccess, isLoading: saveLoading }] =
    useSaveExamFormMutation();

  const fee = calculateFee(exam_standard);

  const searchSchools = (keyword) => {
    if (!keyword || keyword.length < 2) {
      setSchoolSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setIsSearching(true);
    fetch(`${Config.API_URL_LOCAL}exam/searchExamSchools`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keyword }),
    })
      .then((r) => r.json())
      .then((data) => {
        setSchoolSuggestions(data.allSchools || []);
        setShowSuggestions(true);
        setIsSearching(false);
      })
      .catch(() => setIsSearching(false));
  };

  const handleSchoolInputChange = (e) => {
    const val = e.target.value;
    setSchoolInput(val);
    setSchoolSelected(false);
    setSelectedSchoolName('');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchSchools(val), 400);
  };

  const handleSchoolSelect = (school) => {
    setSchoolInput(school.school_name);
    setSelectedSchoolName(school.school_name);
    setSchoolSelected(true);
    setShowSuggestions(false);
  };

  const handleStandardChange = (e) => {
    setExamStandard(e.target.value);
  };

  const validate = () => {
    const errs = {};
    if (!exam_standard) errs.exam_standard = 'इयत्ता निवडा';
    if (!gender) errs.gender = 'लिंग निवडा';
    if (!first_name) errs.first_name = 'नाव आवश्यक आहे';
    else if (!MARATHI_REGEX.test(first_name)) errs.first_name = 'फक्त मराठी अक्षरे लिहा';
    if (!middle_name) errs.middle_name = 'वडिलांचे नाव आवश्यक आहे';
    else if (!MARATHI_REGEX.test(middle_name)) errs.middle_name = 'फक्त मराठी अक्षरे लिहा';
    if (!last_name) errs.last_name = 'आडनाव आवश्यक आहे';
    else if (!MARATHI_REGEX.test(last_name)) errs.last_name = 'फक्त मराठी अक्षरे लिहा';
    if (!mobile_number) errs.mobile_number = 'मोबाईल क्रमांक आवश्यक आहे';
    else if (!/^[6-9]\d{9}$/.test(mobile_number)) errs.mobile_number = 'वैध मोबाईल क्रमांक लिहा';
    if (!schoolSelected) errs.school_name = 'यादीतून शाळा निवडणे आवश्यक आहे';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const clearForm = () => {
    setExamStandard('');
    setGender('');
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setMobileNumber('');
    setSchoolInput('');
    setSelectedSchoolName('');
    setSchoolSelected(false);
    setErrors({});
  };

  useEffect(() => {
    if (saveSuccess) {
      if (saveData?.error) {
        toast.error(saveData.message);
      } else {
        toast.success('फॉर्म यशस्वीरित्या सेव्ह झाला!');
        clearForm();
      }
      setIsSubmitting(false);
    }
  }, [saveData, saveSuccess]);

  const onSubmit = () => {
    if (!validate()) return;
    setIsSubmitting(true);
    SaveExamForm({
      exam_name: examName,
      exam_medium: 'marathi',
      exam_standard,
      gender,
      first_name,
      middle_name,
      last_name,
      mobile_number,
      school_name: selectedSchoolName,
    });
  };

  if (!examName) {
    return (
      <Template>
        <main id="main" className="main">
          <div className="container">
            <div className="alert alert-danger mt-4">
              अवैध परीक्षा प्रकार. कृपया Dashboard वरून पुन्हा येथे या.
            </div>
          </div>
        </main>
      </Template>
    );
  }

  return (
    <Template>
      <ToastContainer />
      <main id="main" className="main">
        <div className="container">
          <div className="row">
            <div className="col-md-6 offset-md-3">
              <div className="card mt-3">
                <div className="card-body">
                  <h5 className="card-title mb-3">{examName}</h5>

                  <div className="form-group">
                    <label>माध्यम</label>
                    <input type="text" className="form-control mb-1" value="मराठी" readOnly disabled style={{ backgroundColor: '#f8f9fa', color: '#495057' }} />
                  </div>

                  <div className="form-group">
                    <label>इयत्ता <span className="red-star">*</span></label>
                    <select value={exam_standard} className="form-control mb-1" onChange={handleStandardChange}>
                      <option value="">Select Standard</option>
                      {standards.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {errors.exam_standard && <small className="text-danger d-block mb-2">{errors.exam_standard}</small>}
                  </div>

                  <div className="form-group">
                    <label>लिंग <span className="red-star">*</span></label>
                    <select value={gender} className="form-control mb-1" onChange={(e) => setGender(e.target.value)}>
                      <option value="">Select Gender</option>
                      <option value="कुमार">कुमार</option>
                      <option value="कुमारी">कुमारी</option>
                    </select>
                    {errors.gender && <small className="text-danger d-block mb-2">{errors.gender}</small>}
                  </div>

                  <div className="form-group">
                    <label>नाव <span className="red-star">*</span></label>
                    <input
                      type="text"
                      className="form-control mb-1"
                      placeholder="मराठी नाव लिहा"
                      value={first_name}
                      onChange={(e) => setFirstName(marathiOnly(e.target.value))}
                    />
                    {errors.first_name && <small className="text-danger d-block mb-2">{errors.first_name}</small>}
                  </div>

                  <div className="form-group">
                    <label>वडिलांचे नाव <span className="red-star">*</span></label>
                    <input
                      type="text"
                      className="form-control mb-1"
                      placeholder="मराठी नाव लिहा"
                      value={middle_name}
                      onChange={(e) => setMiddleName(marathiOnly(e.target.value))}
                    />
                    {errors.middle_name && <small className="text-danger d-block mb-2">{errors.middle_name}</small>}
                  </div>

                  <div className="form-group">
                    <label>आडनाव <span className="red-star">*</span></label>
                    <input
                      type="text"
                      className="form-control mb-1"
                      placeholder="मराठी नाव लिहा"
                      value={last_name}
                      onChange={(e) => setLastName(marathiOnly(e.target.value))}
                    />
                    {errors.last_name && <small className="text-danger d-block mb-2">{errors.last_name}</small>}
                  </div>

                  <div className="form-group">
                    <label>दूरध्वनी क्रमांक <span className="red-star">*</span></label>
                    <input
                      type="text"
                      className="form-control mb-1"
                      placeholder="10 अंकी मोबाईल क्रमांक"
                      maxLength={10}
                      value={mobile_number}
                      onChange={(e) => setMobileNumber(digitsOnly(e.target.value))}
                    />
                    {errors.mobile_number && <small className="text-danger d-block mb-2">{errors.mobile_number}</small>}
                  </div>

                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>शाळेचे नाव <span className="red-star">*</span></label>
                    <input
                      type="text"
                      className="form-control mb-1"
                      placeholder="शाळेचे नाव टाइप करा..."
                      value={schoolInput}
                      onChange={handleSchoolInputChange}
                      onBlur={() => setTimeout(() => {
                        setShowSuggestions(false);
                        if (!schoolSelected) setSchoolInput('');
                      }, 200)}
                      onFocus={() => schoolSuggestions.length > 0 && setShowSuggestions(true)}
                    />
                    {errors.school_name && <small className="text-danger d-block mb-1">{errors.school_name}</small>}
                    {isSearching && <small className="text-muted">शोधत आहे...</small>}
                    {showSuggestions && schoolSuggestions.length > 0 && (
                      <ul
                        className="list-group"
                        style={{
                          position: 'absolute',
                          zIndex: 999,
                          width: '100%',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          top: '100%',
                        }}
                      >
                        {schoolSuggestions.map((school, idx) => (
                          <li
                            key={idx}
                            className="list-group-item list-group-item-action"
                            onMouseDown={() => handleSchoolSelect(school)}
                            style={{ cursor: 'pointer' }}
                          >
                            {school.school_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {fee > 0 && (
                    <div className="alert alert-success mt-3">
                      <strong>परीक्षा शुल्क: ₹{fee}</strong>
                    </div>
                  )}

                  <div className="form-group mt-3">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={onSubmit}
                      disabled={isSubmitting || saveLoading}
                    >
                      {isSubmitting || saveLoading ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </Template>
  );
}

export default ExamFormPage;
